<!--
  A RAIL OF ONE PRIZE'S WINNERS — or several prizes pooled into one row, which
  is how "Festival favourites" says Cannes, Venice and Berlin in one breath.

  The award list is a set of IMDb ids and win years and nothing else — Wikidata
  knows what won and when, it does not know the artwork or the runtime. So this
  asks the catalogue for each id and draws whatever comes back, dropping the ones
  Cinemeta has never heard of. That miss rate is real and expected: the awards go
  back to 1929 and a streaming catalogue does not. The candidates arrive NEWEST
  WIN FIRST (see `titlesWithAny`), which both leads the row with recent winners
  and quietly raises the hit rate — the catalogue knows this decade far better
  than it knows 1953.

  THE ROW RUNS THE WHOLE POOL NOW. The opening fill verifies enough for a
  generous first screen (`OPENING`), and the rail's own `more()` contract
  walks the REST of the candidate list a stretch at a time as the viewer
  approaches the end — Best Picture is 98 winners deep, the Emmys 151 — until
  the pool is spent, at which point the rail loops its survivors. Verification
  stays batched (four catalogue lookups at a time, in order) so a public
  endpoint is never treated like a crawler, and a short pool is a short rail:
  never padding.

  While it fetches it holds the row's height with ghost cards, like `LazyRail` —
  a rail that pops in later shoves the page under a remote's focus (see the note
  above `ghosts`). Once answered, fewer than four survivors renders nothing:
  a rail with two cards in it reads as a bug.
-->
<script lang="ts">
  import type { CatalogItem } from '$types';
  import Poster from './Poster.svelte';
  import Rail from './Rail.svelte';
  import { AWARDS, awardsFor, titlesWithAny, type AwardId } from './awards';
  import { definiteMiss, detail } from './cinemeta';
  import { MORE_RETRY_MS } from './rail-ring';

  interface Props {
    /** One prize, or several pooled into one row. */
    award: AwardId | AwardId[];
    title: string;
    /** Override the hover note; defaults to naming the prize(s). */
    note?: string;
    /**
     * How many the row OPENS with. Each candidate tried is one catalogue
     * lookup, so the opening fill is a budget for the first paint — the rest
     * of the pool is only ever verified when somebody actually scrolls the
     * rail toward its end (`more()`), a stretch at a time.
     */
    limit?: number;
  }

  let { award, title, note, limit = 32 }: Props = $props();

  const ids = $derived<AwardId[]>(Array.isArray(award) ? award : [award]);
  const railNote = $derived(note ?? `Winners of the ${ids.map((a) => AWARDS[a].label).join(' · ')}`);

  /**
   * THE TILE'S CORNER TAG: this rail's own prize, and nothing else (the owner,
   * 2026-08-17: "we only show these tags for rails that are specifically about
   * awards, and we just show that award"). Award rails are the ONLY caller of
   * Poster's `awardBadge` — every other rail's corner belongs to the rating.
   *
   * For a pooled rail the pick is the best pool member THIS title won — which
   * is what keeps the tag both uniform and honest: sibling categories share
   * one code, emblem and tone (every Globe is GG, every Emmy EM), so at rest
   * the row wears the family mark; expanded, the badge spells the precise
   * category the title took ("Golden Globe — Best TV Drama"), never a sibling
   * it didn't. A title is only in this rail because it won a pool member
   * (`titlesWithAny`), so the fallback to ids[0] is for the type, not for use.
   */
  function badgeFor(tt: string): AwardId {
    const pool = new Set<AwardId>(ids);
    return awardsFor(tt).find((a) => pool.has(a.id))?.id ?? ids[0]!;
  }

  /** null while the catalogue is still answering — the placeholder's cue. */
  let items = $state<CatalogItem[] | null>(null);
  let mount = $state<HTMLElement | null>(null);

  /** The WHOLE candidate pool, newest win first, fixed at first load. */
  let candidates: string[] = [];
  const lifetime = new AbortController();
  /** Next candidate to verify. `cursor − items.length` died on the way. */
  let cursor = 0;
  /**
   * Candidates whose lookups ERRORED — the network gone, a 5xx — as opposed
   * to the catalogue cleanly answering "no such title". AN ERRORED CANDIDATE
   * IS NOT A NONEXISTENT CANDIDATE: these are parked for a later walk (each
   * walk gives them one more chance) and, while any are parked, the pool is
   * NOT spent — so a bad moment on a train can never fake the end of Best
   * Picture and freeze the rail short for the night.
   */
  let parked: string[] = [];

  /** How many more survivors one `more()` approach tries to confirm. */
  const STRETCH = 16;

  /*
   * ── ONE WALK AT A TIME ─────────────────────────────────────────────────
   * The opening fill publishes survivors batch-by-batch, so the rail is
   * mounted — and asking through `more()` — while `opening()` is still
   * walking the pool. Two concurrent walks would share `cursor` and `items`:
   * interleaved batches land out of newest-first order, and eight lookups
   * fly where the budget is four. So every walk queues behind the last, and
   * `more()` during the opening fill JOINS it (the opening's own growth is
   * that approach's answer) instead of starting a parallel one.
   */
  let walk: Promise<unknown> = Promise.resolve();
  /** A walk is mid-flight right now — `more()`'s cue to join, not add. */
  let walking = false;
  function queueWalk(want: () => number): Promise<number> {
    const run = async (): Promise<number> => {
      if (lifetime.signal.aborted) return 0;
      walking = true;
      try {
        return await verify(want());
      } finally {
        walking = false;
      }
    };
    const next = walk.then(run, run);
    walk = next;
    return next;
  }

  /*
   * Lazily, like every other rail below the fold: the opening fill is
   * `limit`-odd metadata requests and there is no reason to spend them on a
   * row nobody has scrolled to. The observer fires once and then disconnects.
   */
  $effect(() => {
    const el = mount;
    if (!el || items !== null) return;
    if (typeof IntersectionObserver !== 'function') {
      // No observer, no laziness — but never a placeholder that ghosts forever.
      void opening();
      return;
    }
    let done = false;
    const io = new IntersectionObserver(
      (entries) => {
        if (done || !entries.some((e) => e.isIntersecting)) return;
        done = true;
        io.disconnect();
        void opening();
      },
      { rootMargin: '600px' }
    );
    io.observe(el);
    return () => io.disconnect();
  });

  /*
   * Which kind to ask the catalogue for, PER TITLE, from the medium of the
   * prizes that title actually won. This is not just a guess-first
   * optimisation: Cinemeta's movie endpoint FABRICATES a record for some
   * series ids (metahub resolves the TMDB number in the movie namespace, so
   * `movie/tt11198330` — House of the Dragon — comes back as a 1982 film
   * with the right id and the wrong everything). The series endpoint returns
   * `{}` for a film id rather than inventing one, so a film may safely fall
   * back to series, but a television winner must NEVER fall back to movie —
   * that path is where the junk lives. The cost is that a made-for-TV film
   * with only a TV prize is skipped; a skipped card is cheaper than a wrong
   * one.
   */
  function kindsFor(tt: string): readonly ('movie' | 'series')[] {
    const wanted = new Set<AwardId>(ids);
    const won = awardsFor(tt).filter((a) => wanted.has(a.id));
    const tvOnly = won.length > 0 && won.every((a) => a.medium === 'tv');
    return tvOnly ? ['series'] : ['movie', 'series'];
  }

  /**
   * Verify candidates from the cursor until `want` more survivors are
   * CONFIRMED or the pool runs out — a dead id costs a read but not a card.
   * Small batches, in order, four at a time: firing a pool's worth of
   * catalogue requests at once to fill one rail is rude to a public endpoint.
   * Batch-by-batch also keeps the newest-first order the candidates arrived
   * in, however the answers race back. Returns how many cards were added.
   *
   * Only ever called through `queueWalk` — one walk at a time, or two would
   * interleave over one cursor.
   */
  async function verify(want: number): Promise<number> {
    const width = 4;
    /* This walk's bounded second chance: candidates whose lookups ERRORED on
       an earlier walk rejoin the queue behind the fresh ones, at most once
       per walk — one that errors AGAIN goes back to `parked`, which this
       walk never re-reads, so a dead network cannot spin this loop. */
    const second = parked;
    parked = [];
    /* Confirmed survivors not yet published. Held back only while the rail is
       still a placeholder: the render threshold below is four, and publishing
       two would collapse the ghosts into nothing for a beat. */
    let buffer: CatalogItem[] = [];
    let added = 0;
    while (!lifetime.signal.aborted && added < want && (cursor < candidates.length || second.length > 0)) {
      const batch: string[] = [];
      while (batch.length < width && cursor < candidates.length) {
        batch.push(candidates[cursor]!);
        cursor += 1;
      }
      while (batch.length < width && second.length > 0) batch.push(second.shift()!);
      const results = await Promise.all(
        batch.map(async (id) => {
          let errored = false;
          for (const kind of kindsFor(id)) {
            if (lifetime.signal.aborted) return null;
            try {
              const d = await detail(kind, id, lifetime.signal);
              if (lifetime.signal.aborted) return null;
              if (d) return d;
            } catch (err) {
              /* A clean 404 is the catalogue's ANSWER — not under this kind;
                 try the next, then count the candidate out. Anything else is
                 a bad moment, not a fact about the title: park it. */
              if (!definiteMiss(err)) errored = true;
            }
          }
          if (!lifetime.signal.aborted && errored) parked.push(id);
          return null;
        })
      );
      if (lifetime.signal.aborted) return 0;
      for (const d of results) {
        if (d) {
          buffer.push(d);
          added += 1;
        }
      }
      /*
       * PUBLISH AS THE BATCHES LAND, once there is enough to be a rail. The
       * opening fill at 32 takes seconds against a public endpoint, and
       * seconds of ghost cards over a row that already KNOWS its first dozen
       * winners is a lie of omission. Appending to a horizontal row moves
       * nothing vertically — the ghosts held the height — so each batch is
       * cards fading in at the end, not a page shifting under a remote's
       * focus.
       */
      if (buffer.length > 0 && (items !== null || buffer.length >= 4)) {
        items = [...(items ?? []), ...buffer];
        buffer = [];
      }
    }
    if (lifetime.signal.aborted) return 0;
    /* Second chances this walk never reached stay parked for the next one. */
    parked.push(...second);
    if (buffer.length > 0) items = [...(items ?? []), ...buffer];
    return added;
  }

  /** True when every candidate has been CLEANLY answered — the only state
   *  that may ever read as the pool's end. Errored candidates keep it open. */
  function spent(): boolean {
    return cursor >= candidates.length && parked.length === 0;
  }

  /** After a starved opening walk, when the next one runs — the rail-level
   *  cooldown (MORE_RETRY_MS), applied to the fill that has no mounted Rail
   *  to retry for itself yet. */
  let openTimer: ReturnType<typeof setTimeout> | null = null;
  $effect(() => () => {
    lifetime.abort();
    if (openTimer !== null) clearTimeout(openTimer);
  });

  /** The opening fill: the pool pinned once, then walked to the first
   *  `limit` survivors. */
  async function opening(): Promise<void> {
    if (lifetime.signal.aborted) return;
    candidates = titlesWithAny(ids);
    cursor = 0;
    parked = [];
    await openingWalk();
  }

  /**
   * One opening walk. Three ways out: the rail RENDERS (four survivors —
   * `more()` carries on from there); the pool is CLEANLY spent (whatever
   * survived is the honest answer, and fewer than four collapses the rail
   * exactly as before); or errored candidates are what is starving it — a
   * network blip, not a short pool — and the walk runs again after the same
   * cooldown a mounted rail's rejected `more()` gets.
   */
  async function openingWalk(): Promise<void> {
    await queueWalk(() => Math.max(0, limit - (items?.length ?? 0)));
    if (lifetime.signal.aborted) return;
    if (items !== null && items.length >= 4) return;
    if (spent()) {
      items = items ?? [];
      return;
    }
    openTimer = setTimeout(() => {
      openTimer = null;
      void openingWalk();
    }, MORE_RETRY_MS);
  }

  /**
   * THE RAIL'S `more()`: the next stretch of the pool, verified on approach.
   * The rail is mounted — and can ask — while the opening fill is still
   * walking (it publishes batch-by-batch), so an ask that finds a walk in
   * flight JOINS it and reports its growth, rather than racing a second
   * cursor-walk alongside (one shared queue, `queueWalk`).
   *
   * 'end' ONLY FROM CLEAN ANSWERS: once every candidate has truly answered,
   * the rail loops its survivors. A stretch that confirmed nothing while
   * errored candidates sit parked THROWS instead — Rail treats a rejection
   * as transient (the ghost stays, a cooldown, a retry), which is exactly
   * what an errored walk is. It is never exhaustion.
   */
  async function more(): Promise<'grew' | 'end'> {
    lifetime.signal.throwIfAborted();
    const before = items?.length ?? 0;
    if (walking) {
      await walk.catch(() => {});
      lifetime.signal.throwIfAborted();
      if ((items?.length ?? 0) > before) return 'grew';
    }
    if (spent()) return 'end';
    const added = await queueWalk(() => STRETCH);
    lifetime.signal.throwIfAborted();
    if (added > 0) return 'grew';
    if (spent()) return 'end';
    throw new Error('the stretch errored; the pool is not spent');
  }

  /*
   * THE PLACEHOLDER HOLDS THE ROW'S HEIGHT, exactly as `LazyRail`'s does — and
   * here it is not a nicety but a navigation fix. This rail's lookups are
   * sequential batches against a public endpoint, so it answers SECONDS after
   * it is scrolled near; rendering nothing until then meant a whole rail's
   * height popping into the page mid-traversal. Measured on Home at 1280x720
   * with a remote: the focused plate below was shoved fully off screen, and the
   * next Up had to scroll the page DOWN 367px to reach the row that had just
   * materialised. Ghosts first, pictures when they come; only a rail that
   * answers short (fewer than four survivors) collapses, which is rare and a
   * single settle rather than six. `loop={false}`: shimmer never loops.
   */
  const ghosts = Array.from({ length: 7 }, (_, i) => i);
</script>

{#if items === null}
  <div bind:this={mount} aria-busy="true">
    <Rail {title} note={railNote} loop={false}>
      {#each ghosts as n (n)}
        <Poster ghost />
      {/each}
    </Rail>
  </div>
{:else if items.length >= 4}
  <Rail {title} note={railNote} {items} {more}>
    {#snippet tile(item: CatalogItem)}
      <Poster {item} awardBadge={badgeFor(item.id)} />
    {/snippet}
  </Rail>
{/if}
